import type { App } from "vue";

import * as Sentry from "@sentry/vue";
import { withScope } from "@sentry/vue";

import {
  flushSentryBuffer,
  getEnv,
  isBot,
  isSupportedBrowser,
} from "@solvari/utils";

import type { SentryRootProject } from "@/lib/integrations/sentry/types";

import { ignoreConfig } from "@/lib/integrations/sentry/ignored.ts";
import {
  getEventProject,
  solvariContextIntegration,
  solvariFilterIntegration,
  solvariMicroFeContextIntegration,
  solvariModuleMetaIntegration,
  solvariUrlFragmentScrubIntegration,
} from "@/lib/integrations/sentry/integrations.ts";
import {
  rootProjects,
  sentryProjects,
} from "@/lib/integrations/sentry/projects.ts";
import { trackImageLoading } from "@/lib/integrations/sentry/trackers.ts";

/*
  You can only have one Sentry instance running on a page.
  We use micro frontends that each have their own Sentry project.
  So we need some logic to send the errors to the right project by defining them globally
 */

function getProjectRelease(project: string) {
  // We never increment the package versions, so we just ignore them as it adds complexity
  return `${project}@unknown`;
}

function makeMicroFrontEndTransport() {
  return Sentry.makeMultiplexedTransport(
    Sentry.makeFetchTransport,
    ({ getEvent }) => {
      const event = getEvent();
      if (!event) {
        return [];
      }
      const project = (event.tags?.project ||
        getEventProject(event)?.project) as string | undefined;
      if (!project || !(project in sentryProjects)) {
        return [];
      }
      return [
        {
          dsn: sentryProjects[project as keyof typeof sentryProjects],
          release: getProjectRelease(project),
        },
      ];
    },
  );
}

function initSentryRoot(rootProject: SentryRootProject) {
  if (!isSupportedBrowser() || isBot() || getEnv().config.envType === "local") {
    return;
  }

  Sentry.init({
    dsn: rootProjects[rootProject.project],
    environment: getEnv().config.envType,
    transport: makeMicroFrontEndTransport(),
    release: getProjectRelease(rootProject.project),
    sendDefaultPii: true,
    integrations: (integrations) => [
      ...integrations.filter((integration) => integration.name !== "Vue"),
      Sentry.extraErrorDataIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.moduleMetadataIntegration(),
      solvariFilterIntegration(),
      solvariContextIntegration(rootProject),
      solvariModuleMetaIntegration(),
      solvariMicroFeContextIntegration(),
      ...(rootProject.stripUrlFragments
        ? [solvariUrlFragmentScrubIntegration()]
        : []),
    ],
    ...ignoreConfig,
  });

  const captureLog = (...args: Parameters<typeof Sentry.captureException>) => {
    withScope((scope) => {
      scope.setTag("project", "logs-fe");
      scope.setTag("package", rootProject.project);
      const [error, hint] = args;
      if (hint && typeof hint === "object" && "tags" in hint && hint.tags) {
        const { project: _project, ...tags } = hint.tags;
        Sentry.captureException(error, { ...hint, tags });
      } else {
        Sentry.captureException(error, hint);
      }
    });
  };

  globalThis.sentry = {
    captureLog,
    captureException: Sentry.captureException,
    trackVueErrors: (app: App) => {
      Sentry.addIntegration(Sentry.vueIntegration({ app }));
    },
  };

  flushSentryBuffer();

  if (getEnv().config.envType === "production") {
    trackImageLoading();
  }
}

export { initSentryRoot };
